import SlideOver from '#components/slide-over.jsx';
import Button from '#lib/components/button.jsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PlansDiff from './plans-diff.jsx';
import { DataDiff } from './data-diff.jsx';
import DaysDiff from './days-diff.jsx';
import { checkHeadersAndDocs, checkIfDiffExist } from './helpers.js';
import { RowDiff } from './row-diff.jsx';
import { PlanTemplate, TemplateData, TemplateDiffData } from '#routes/manage/templates/template-details.jsx';
import { DiffOverviewCell, PlanRow } from '#@itinerary/planner/days-overview.jsx';
import { useEffect, useRef, useState } from 'react';
import { addClasses, cn } from '#lib/helpers/class.js';
import { group, sameIdOrImportId, ungroup } from '#@itinerary/places/helpers.js';
import { Row, RowTitle } from '#@itinerary/planner/row.jsx';
import { Icon } from '@iconify-icon/react/dist/iconify.mjs';
import { SuggestionIcon } from '#components/suggestion-icon.jsx';
import { GalleryEditor } from '#@itinerary/gallery/gallery-editor.jsx';
import { useGallery } from '#@itinerary/gallery/use-gallery.js';
import { ImageDiff } from './image-diff.jsx';
import { AudioDiff } from './audio-diff.jsx';

export function SlideOverDiff(props) {
    const {
        isOpenSlideOver,
        panelStateList,
        panelStateIndex,
        goBack,
        goAhead,
        incomingHeader,
        incomingDocs,
        incomingPlanner,
        incomingPlans,
        incomingPlaces,
        form,
        // CurrentData
        currentHeader,
        currentDocuments,
        currentDays,
        currentPlans,
        currentPlaces,
        // Updated data
        updatedHeader,
        setUpdatedHeader,
        updatedDocuments,
        setUpdatedDocuments,
        updatedDays,
        updatedPlans,
        updatedPlaces,
        setUpdatedPlaces,
        discardChanges,
        totalFilters
    } = props;

    const panelState = panelStateList[panelStateIndex];

    if (!panelState) return null;
    useEffect(() => {
        if (isOpenSlideOver) {
            document.getElementById('root').removeAttribute('inert');
            document.getElementById('root').removeAttribute('aria-hidden');
        }
    }, [isOpenSlideOver]);

    return (
        <SlideOver isOpen={isOpenSlideOver} onClose={() => {}} width="2xl">
            <div className="w-full bg-white gap-4 flex flex-col" style={{ height: 'calc(100vh - 90px)' }}>
                <div>
                    <div className="flex justify-between items-center border-b border-neutral-300">
                        <div>
                            <h2 className="text-2xl font-semibold mb-2">
                                {panelState === 'summary' && _t.edit.updateItinerary.summary()}
                                {panelState === 'header' && _t.edit.updateItinerary.updateHeaderAndDocsTitle()}
                                {panelState === 'days' && _t.edit.updateItinerary.reorderPlansTitle()}
                                {panelState === 'plans' && _t.edit.updateItinerary.updatePlannerTitle()}
                                {panelState === 'places' && _t.edit.updateItinerary.updatePlacesTitle()}
                            </h2>
                            <p className="text-neutral-700">
                                {panelState === 'summary' && _t.edit.updateItinerary.summaryDescription()}
                                {panelState === 'header' && _t.edit.updateItinerary.updateHeaderAndDocsDescription()}
                                {panelState === 'days' && _t.edit.updateItinerary.reorderPlansDescription()}
                                {panelState === 'plans' && _t.edit.updateItinerary.updatePlannerDescription()}
                                {panelState === 'places' && _t.edit.updateItinerary.updatePlacesDescription()}
                            </p>
                        </div>
                        <button onClick={discardChanges}>
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>
                <>
                    {panelState === 'header' && (
                        <HeaderAndDocsDiff
                            form={form}
                            incomingHeader={incomingHeader}
                            incomingDocs={incomingDocs}
                            currentHeader={currentHeader}
                            currentDocuments={currentDocuments}
                            updatedHeader={updatedHeader}
                            setUpdatedHeader={setUpdatedHeader}
                            updatedDocuments={updatedDocuments}
                            setUpdatedDocuments={setUpdatedDocuments}
                        />
                    )}
                    {panelState === 'days' && (
                        <DaysDiff
                            updatedPlaces={updatedPlaces}
                            incomingPlaces={incomingPlaces}
                            filters={totalFilters}
                            incomingDays={incomingPlanner}
                            incomingPlans={incomingPlans}
                            currentDays={currentDays}
                            currentPlans={currentPlans}
                            updatedDays={updatedDays}
                            updatedPlans={updatedPlans}
                            documents={updatedDocuments}
                            {...props}
                        />
                    )}
                    {panelState === 'plans' && (
                        <PlannerDiff
                            incomingPlanner={incomingPlanner}
                            incomingDocs={incomingDocs}
                            currentPlans={currentPlans}
                            updatedPlans={updatedPlans}
                            places={updatedPlaces}
                            filters={totalFilters}
                            days={updatedDays}
                            plans={updatedPlans}
                            documents={updatedDocuments}
                            {...props}
                        />
                    )}
                    {panelState === 'places' && (
                        <PlacesDiff
                            incomingPlaces={incomingPlaces}
                            currentPlaces={currentPlaces}
                            updatedPlaces={updatedPlaces}
                            setUpdatedPlaces={setUpdatedPlaces}
                        />
                    )}
                    {panelState === 'summary' && <SummaryPanel {...props} />}
                </>
                <div className="flex justify-between items-center gap-2 px-2">
                    <div className="flex justify-between items-center mt-2 pr-1">
                        <span className="text-lg font-semibold text-neutral-700">
                            {panelStateIndex + 1} / {panelStateList.length}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {panelStateIndex !== 0 && <Button onClick={goBack}>{_t.edit.updateItinerary.back()}</Button>}
                        <Button onClick={goAhead} look={panelState === 'summary' ? 'primary' : 'white'}>
                            {panelState === 'summary' ? _t.global.save() : _t.edit.updateItinerary.next()}
                        </Button>
                    </div>
                </div>
            </div>
        </SlideOver>
    );
}

function HeaderAndDocsDiff(props) {
    const {
        incomingHeader,
        incomingDocs,
        currentHeader,
        updatedHeader,
        setUpdatedHeader,
        currentDocuments,
        updatedDocuments,
        setUpdatedDocuments
    } = props;

    const filteredIncomingDocs = incomingDocs.filter((doc) => !currentDocuments?.some((d) => d.id === doc.id));
    const hasIncomingDocs = filteredIncomingDocs && filteredIncomingDocs.length > 0;
    const incomingMedia = [
        { type: 'cover', ...incomingHeader?.cover },
        { type: 'intro', ...incomingHeader?.intro },
        { type: 'jingle', ...incomingHeader?.jingle }
    ];
    const filteredIncomingMedia = incomingMedia.filter(
        (media) => media?.src && media.src !== currentHeader[media.type]?.src
    );
    const hasIncomingMedia = filteredIncomingMedia.length > 0;

    const headerDiff = checkHeadersAndDocs(incomingHeader, currentHeader);

    const dataDiffCount = Object.keys(incomingHeader).filter(
        (key) => incomingHeader[key] !== currentHeader[key]
    ).length;

    return (
        <div className="flex-grow overflow-auto">
            <div className="w-full lg:border lg:border-neutral-300 lg:rounded-lg lg:shadow-md p-3 overflow-auto">
                {!headerDiff && (
                    <p className="w-full col-span-full p-4 bg-neutral-100 rounded-md text-center text-neutral-700 font-semibold">
                        {_t.edit.updateItinerary.noDiffText('header')}
                    </p>
                )}
                <div className={cn('grid gap-4', dataDiffCount === 1 ? 'lg:grid-cols-1' : 'lg:grid-cols-2')}>
                    <DataDiff
                        incomingData={incomingHeader}
                        updatedData={updatedHeader}
                        currentData={currentHeader}
                        onUpdateData={(key) => {
                            if (updatedHeader[key] === incomingHeader[key]) return;
                            setUpdatedHeader({ ...updatedHeader, [key]: incomingHeader[key] });
                        }}
                        onRestoreData={(key) => {
                            if (updatedHeader[key] === currentHeader[key]) return;
                            setUpdatedHeader({ ...updatedHeader, [key]: currentHeader[key] });
                        }}
                    />
                </div>
                {hasIncomingDocs && (
                    <div>
                        <p className="pt-4 pb-2 font-semibold">{_t.edit.updateItinerary.incomingDocs()}</p>
                        <div className="flex flex-wrap gap-4">
                            {filteredIncomingDocs.map((doc) => {
                                const docIsPresent = updatedDocuments.some((d) => d.id === doc.id);
                                return (
                                    <RowDiff
                                        key={doc.id}
                                        el={doc}
                                        elIsPresent={docIsPresent}
                                        onUpdateElement={() => {
                                            setUpdatedDocuments((prev) => [...prev, doc]);
                                            toast.success(_t.edit.updateItinerary.addedDocument('itinerary'));
                                        }}
                                        onRestoreElement={() => {
                                            const updated = updatedDocuments.filter((d) => d.id !== doc.id);
                                            setUpdatedDocuments(updated);
                                            toast.success(_t.edit.updateItinerary.removedDocument('itinerary'));
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
                {hasIncomingMedia && (
                    <div>
                        <p className="pt-4 font-semibold">{_t.edit.updateItinerary.incomingMedia()}</p>
                        <div className="grid lg:grid-cols-3 gap-4">
                            {filteredIncomingMedia.map((media) => {
                                if (media.type === 'jingle') {
                                    return (
                                        <AudioDiff
                                            key={media.type}
                                            incomingMedia={media}
                                            updatedMedia={updatedHeader[media.type]}
                                            onUpdateData={() => {
                                                if (updatedHeader[media?.type] === incomingHeader[media?.type]) return;
                                                setUpdatedHeader({
                                                    ...updatedHeader,
                                                    [media?.type]: incomingHeader[media?.type]
                                                });
                                            }}
                                            onRestoreData={() => {
                                                if (updatedHeader[media?.type] === currentHeader[media?.type]) return;
                                                setUpdatedHeader({
                                                    ...updatedHeader,
                                                    [media?.type]: currentHeader[media?.type]
                                                });
                                            }}
                                        />
                                    );
                                }
                                return (
                                    <ImageDiff
                                        key={media.type}
                                        type={media.type}
                                        incomingMedia={media}
                                        updatedMedia={updatedHeader[media.type]}
                                        onUpdateData={() => {
                                            if (updatedHeader[media?.type] === incomingHeader[media?.type]) return;
                                            setUpdatedHeader({
                                                ...updatedHeader,
                                                [media?.type]: incomingHeader[media?.type]
                                            });
                                        }}
                                        onRestoreData={() => {
                                            if (updatedHeader[media?.type] === currentHeader[media?.type]) return;
                                            setUpdatedHeader({
                                                ...updatedHeader,
                                                [media?.type]: currentHeader[media?.type]
                                            });
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function PlannerDiff(props) {
    const { incomingPlanner, incomingDocs, currentPlans, places, totalFilters, days, updatedPlans, documents } = props;

    // Piani in arrivo
    const incomingPlans = incomingPlanner.flatMap((day) => day.plans);

    return (
        <PlansDiff
            places={places}
            filters={totalFilters}
            days={days}
            currentPlans={currentPlans}
            updatedPlans={updatedPlans}
            documents={documents}
            incomingPlans={incomingPlans}
            incomingDocs={incomingDocs}
            {...props}
        />
    );
}

function SummaryPanel(props) {
    const { currentHeader, currentPlans, updatedHeader, updatedDays, updatedPlans, updatedPlaces } = props;

    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [showHeader, setShowHeader] = useState(window.innerWidth > 1024);

    //MANAGE DETAIL
    let Detail;
    if (selectedPlanId) {
        const plan = currentPlans?.find((plan) => sameIdOrImportId(plan, selectedPlanId));
        const updatedPlan = updatedPlans?.find((plan) => sameIdOrImportId(plan, selectedPlanId));
        // Salva la posizione dello scroll attuale
        if (updatedPlan)
            // Impedisci lo scroll impostando la posizione dello scroll allo stato precedente
            Detail = <PlanTemplate summaryMode={true} currentData={plan} {...updatedPlan} />;
    }

    const summaryRef = useRef(null);
    const summaryHeight = summaryRef.current?.clientHeight;
    const detailRef = useRef(null);
    const detailHeight = detailRef.current?.clientHeight;

    return (
        <div className="w-full flex-grow lg:overflow-hidden overflow-auto grid grid-cols-3 gap-2" ref={summaryRef}>
            <div
                className="col-span-full lg:col-span-1 p-3 flex flex-col justify-start gap-6 lg:border lg:border-neutral-300 lg:rounded-lg lg:shadow-md lg:overflow-auto"
                style={{ height: window.innerHeight > 1024 ? summaryHeight : 'auto' }}
            >
                <p className="text-lg font-semibold">{_t.edit.updateItinerary.updatedHeader()}</p>
                {showHeader && (
                    <div className="w-full flex flex-col items-center justify-start gap-6">
                        {Object.keys(updatedHeader).map((key) => {
                            return (
                                <TemplateDiffData
                                    key={key}
                                    labelKey={key}
                                    label={_t.edit.fields[key]()}
                                    oldValue={currentHeader[key]}
                                    value={updatedHeader[key]}
                                />
                            );
                        })}
                    </div>
                )}
                <div className="block lg:hidden">
                    <Button
                        onClick={() => {
                            setShowHeader(!showHeader);
                        }}
                    >
                        {showHeader ? _t.edit.updateItinerary.hideHeader() : _t.edit.updateItinerary.showHeader()}
                    </Button>
                </div>
            </div>
            <div
                className="col-span-full lg:col-span-2 h-full p-3 flex flex-col gap-6 lg:border lg:border-neutral-300 lg:rounded-lg lg:shadow-md lg:overflow-auto"
                style={{ height: window.innerHeight > 1024 ? summaryHeight : 'auto' }}
            >
                <p className=" text-lg font-semibold">{_t.edit.updateItinerary.updatedPlanner()}</p>
                <div className="w-full flex-1 grid grid-cols-2 gap-2 lg:overflow-hidden" ref={detailRef}>
                    <div
                        className="col-span-full order-2 lg:order-1 lg:col-span-1 lg:overflow-auto"
                        style={{ height: window.innerHeight > 1024 ? detailHeight : 'auto' }}
                    >
                        {updatedDays?.map((day, dayIndex) => {
                            return (
                                <DiffOverviewCell
                                    {...day}
                                    key={day.id}
                                    selfId={day.id}
                                    hasChild={true}
                                    dayIndex={dayIndex}
                                >
                                    <ol className="w-full isolate">
                                        {day.plans.map((plan) => {
                                            const selected = plan.id === selectedPlanId;
                                            const currentPlan = currentPlans.find((p) => p.importId === plan.importId);
                                            let diffExists = false;
                                            if (plan.importId) diffExists = checkIfDiffExist(plan, currentPlan, 'plan');

                                            return (
                                                <PlanRow
                                                    key={plan?.id || plan?.importId}
                                                    selfId={plan?.id || plan?.importId}
                                                    parentId={day.id}
                                                    importId={plan?.importId}
                                                    selected={selected}
                                                    onSelect={() => setSelectedPlanId(plan?.id || plan?.importId)}
                                                    isDeletable={false}
                                                    isUpdated={diffExists}
                                                    places={group(updatedPlaces)}
                                                    {...plan}
                                                />
                                            );
                                        })}
                                    </ol>
                                </DiffOverviewCell>
                            );
                        })}
                    </div>
                    <div
                        className={cn(
                            'col-span-full lg:col-span-1 bg-neutral-100 lg:bg-white lg:overflow-auto order-1 lg:order-2',
                            !Detail && 'hidden lg:block'
                        )}
                        style={{ height: window.innerHeight > 1024 && Detail ? detailHeight : 'auto' }}
                    >
                        {Detail ? (
                            Detail
                        ) : (
                            <section className="relative h-full">
                                <div className="absolute top-0 left-0 w-full h-full bg-neutral-50 flex justify-center items-center">
                                    <p className="w-2/3 text-center text-neutral-400 italic">
                                        {_t.edit.updateItinerary.detailText()}
                                    </p>
                                </div>
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PlacesDiff(props) {
    const { incomingPlaces: ungroupedIncPlaces, currentPlaces, updatedPlaces, setUpdatedPlaces } = props;
    const incomingPlaces = group(ungroupedIncPlaces);
    const [selectedId, setSelectedId] = useState(null);

    const onAdd = (place) => {
        const updated = structuredClone(updatedPlaces);
        updated.push(place);
        setUpdatedPlaces(updated);
    };

    const onDelete = (place) => {
        const updated = structuredClone(updatedPlaces);
        const index = updated.findIndex((upd) => upd.importId === place.importId);
        updated.splice(index, 1);
        setUpdatedPlaces(updated);
    };

    const onSelect = (id) => setSelectedId(id);

    let Detail;
    if (selectedId) {
        const place = ungroupedIncPlaces?.find((place) => place?.importId === selectedId);
        // Salva la posizione dello scroll attuale
        if (place)
            // Impedisci lo scroll impostando la posizione dello scroll allo stato precedente
            Detail = (
                <>
                    <PlaceDiffDetail {...place} />
                </>
            );
    }

    return (
        <section className="flex flex-col col-span-full lg:border lg:border-neutral-300 lg:rounded-lg lg:shadow-md p-3 h-full w-full">
            <div className={cn('grid grid-cols-1 lg:grid-cols-12 col-span-full xl:divide-x divide-neutral-200 mb-4')}>
                <div
                    className="grid col-span-full xl:col-span-6 divide-x divide-neutral-200"
                    style={{ maxHeight: '0%' }}
                >
                    <div className="col-span-full pt-1">
                        {incomingPlaces?.map((city) => {
                            return (
                                <div key={city?.id}>
                                    <p className="font-semibold text-lg mb-2 border-b border-neutral-200">
                                        {city.name}
                                    </p>

                                    <ol className="w-full isolate flex flex-col gap-2">
                                        <div>
                                            {city.info.length > 0 && (
                                                <p className="font-semibold text-md mb-2 border-b border-neutral-200">
                                                    {_t.edit.filters.Info()}
                                                </p>
                                            )}
                                            {city.info.map((info) => {
                                                const selected = selectedId === info.importId;
                                                return (
                                                    <PlaceRowDiff
                                                        key={info?.id}
                                                        selected={selected}
                                                        onSelect={onSelect}
                                                        isDeletable={false}
                                                        incoming={
                                                            !updatedPlaces.some(
                                                                (place) => place.importId === info.importId
                                                            )
                                                        }
                                                        deletable={
                                                            updatedPlaces.some(
                                                                (place) => place.importId === info.importId
                                                            ) &&
                                                            !ungroup(currentPlaces).some(
                                                                (place) => place.importId === info.importId
                                                            )
                                                        }
                                                        onAdd={() => onAdd(info)}
                                                        onDelete={() => onDelete(info)}
                                                        {...info}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div>
                                            {city.suggestions.length > 0 && (
                                                <p className="font-semibold text-md mb-2 border-b border-neutral-200">
                                                    {_t.edit.filters.suggestions()}
                                                </p>
                                            )}
                                            {city.suggestions.map((suggestion) => {
                                                const selected = selectedId === suggestion.importId;
                                                return (
                                                    <PlaceRowDiff
                                                        key={suggestion?.id}
                                                        selected={selected}
                                                        onSelect={onSelect}
                                                        isDeletable={false}
                                                        incoming={
                                                            !updatedPlaces.some(
                                                                (place) => place.importId === suggestion.importId
                                                            )
                                                        }
                                                        deletable={
                                                            updatedPlaces.some(
                                                                (place) => place.importId === suggestion.importId
                                                            ) &&
                                                            !ungroup(currentPlaces).some(
                                                                (place) => place.importId === suggestion.importId
                                                            )
                                                        }
                                                        onAdd={() => onAdd(suggestion)}
                                                        onDelete={() => onDelete(suggestion)}
                                                        {...suggestion}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </ol>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="col-span-full xl:col-span-6 bg-neutral-100 xl:bg-white mt-8 xl:mt-0">
                    {Detail ? (
                        Detail
                    ) : (
                        <>
                            <section className="hidden xl:block relative h-full">
                                <div className="absolute top-0 left-0 w-full h-full bg-neutral-50 flex justify-center items-center">
                                    <p className="w-2/3 text-center text-neutral-400 italic">
                                        {_t.edit.planner.detailText()}
                                    </p>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}

function PlaceRowDiff(props) {
    const {
        selected,
        type,
        category,
        name,
        importId,
        className,
        onSelect,
        onAdd,
        onDelete,
        incoming,
        deletable,
        as = 'div',
        ref,
        style
    } = props;

    return (
        <Row
            as={as}
            ref={ref}
            style={{ ...style }}
            className={addClasses(
                cn(
                    'relative pl-2 pr-1 py-1 my-1 hover:text-primary-500 transition-all',
                    (incoming || deletable) && 'hover:bg-primary-100',
                    incoming && 'text-neutral-400',
                    selected && 'text-primary-500'
                )
            )}
            selected={selected}
        >
            <div className={cn('w-full flex justify-between items-center gap-x-6')}>
                <div
                    className={cn(
                        'w-full flex justify-start items-center gap-x-2 hover:text-primary-600',
                        selected && 'text-primary-600'
                    )}
                    onClick={() => onSelect(importId)}
                >
                    {type === 'info' ? (
                        <Icon icon="material-symbols:info-outline-rounded" />
                    ) : (
                        <SuggestionIcon category={category} />
                    )}
                    {name && <RowTitle className="w-full overflow-hidden truncate text-ellipsis">{name}</RowTitle>}
                </div>
                {incoming && (
                    <Button className="text-neutral-700 hover:text-primary-500 pr-2" onClick={onAdd}>
                        <Icon className="cursor-pointer pt-[2px]" icon="ic:round-add" width="1.3rem" height="1.3rem" />
                    </Button>
                )}
                {deletable && (
                    <Button className="text-neutral-700 hover:text-primary-500 pr-2" onClick={onDelete}>
                        <Icon
                            className="cursor-pointer pt-[2px]"
                            icon="ic:round-remove"
                            width="1.3rem"
                            height="1.3rem"
                        />
                    </Button>
                )}
            </div>
        </Row>
    );
}

function PlaceDiffDetail(props) {
    const { importId, name, description, gallery, type, address, distance, rating } = props;
    const galleryProps = useGallery({
        initialData: gallery
    });
    if (type === 'info')
        return (
            <div className="flex flex-col gap-2 mt-2 ml-2">
                {name && <TemplateData label={_t.edit.fields.name()} value={name} />}
                {description && <TemplateData label={_t.edit.fields.description()} value={description} />}
            </div>
        );
    return (
        <div className="flex flex-col gap-2 ml-2">
            {gallery && gallery.length > 0 && <GalleryEditor {...galleryProps} id={importId} template={true} />}
            {name && <TemplateData label={_t.edit.fields.name()} value={name} />}
            {description && <TemplateData label={_t.edit.fields.description()} value={description} />}
            {address && <TemplateData label={_t.edit.fields.address()} value={address} />}
            <div className="flex gap-2 justify-between">
                {distance && <TemplateData label={_t.edit.fields.distance()} value={distance} />}
                {rating && <TemplateData label={_t.edit.fields.rating()} value={rating} />}
            </div>
        </div>
    );
}
